'use client';

import { DictionarySubDirectories } from '@/helpers/rendered-page-creation/get-url-subdirectories';
import { INewsletterFormData } from '@/lib/fragments/blockNewsletter';
import { ILibraryMeetingPlaceConstantsDictionary } from '@/lib/fragments/libraryMeetingPlaceDictionaryConstants';
import { ActiveEventItem } from '@/lib/queries/getActiveEventIdsWithTenantIds';
import { ActiveOfferItem } from '@/lib/queries/getActiveOfferIdsWithTenantIds';
import { createContext, useContext } from 'react';

interface ConfigContextType {
  config: Record<string, any>;
  urlSubDirectorydictionary?: DictionarySubDirectories[];
  activeOffers?: ActiveOfferItem[];
  activeEvents?: ActiveEventItem[];
  languageArray?: string[];
  dictionaryConstants?: ILibraryMeetingPlaceConstantsDictionary[];
  newsletterData?: INewsletterFormData | null;
  preview?: boolean;
  timezone?: string;
}

const ConfigContext = createContext<ConfigContextType | null>(null);

export function ConfigProvider({
  config,
  urlSubDirectorydictionary,
  activeOffers,
  activeEvents,
  languageArray,
  dictionaryConstants,
  newsletterData,
  preview = false,
  timezone = '',
  children,
}: {
  config: Record<string, string>;
  urlSubDirectorydictionary?: DictionarySubDirectories[];
  activeOffers?: ActiveOfferItem[];
  activeEvents?: ActiveEventItem[];
  languageArray?: string[];
  dictionaryConstants?: ILibraryMeetingPlaceConstantsDictionary[];
  newsletterData?: INewsletterFormData | null;
  preview?: boolean;
  timezone?: string;
  children: React.ReactNode;
}) {
  return (
    <ConfigContext.Provider
      value={{
        config,
        urlSubDirectorydictionary,
        activeOffers,
        activeEvents,
        languageArray,
        dictionaryConstants,
        newsletterData,
        preview,
        timezone,
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
}

export const useTenantConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    // // Enhanced error with stack trace
    // const error = new Error('useConfig must be used within ConfigProvider');
    // console.error('ConfigProvider Error:', error);
    // console.trace('Stack trace:');
    // throw error;
    throw new Error('useTenantConfig must be used within ConfigProvider');
  }
  return context.config;
};

export const useUrlSubDirectoryDictionary = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error(
      'urlSubDirectorydictionary must be used within ConfigProvider',
    );
  }
  return context.urlSubDirectorydictionary || [];
};

export const useGetActiveOffers = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useGetActiveOffers must be used within ConfigProvider');
  }
  return context.activeOffers || [];
};

export const useGetActiveEvents = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useGetActiveOEvents must be used within ConfigProvider');
  }
  return context.activeEvents || [];
};

export const useGetLanguageArray = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useGetLanguageArray must be used within ConfigProvider');
  }
  return context.languageArray || [];
};

export const useGetNewsletterFormData = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useGetLanguageArray must be used within ConfigProvider');
  }
  return context.newsletterData || null;
};

export const useGetLibraryConstantsDictionaryData = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useGetLanguageArray must be used within ConfigProvider');
  }
  const items = context.dictionaryConstants?.reduce(
    (acc: any, { key, value }) => {
      acc[key] = value;
      return acc;
    },
    {},
  );
  return items || [];
};

export const useGetPreviewMode = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useGetLanguageArray must be used within ConfigProvider');
  }
  return context.preview || false;
};

export const useGetTimezone = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useGetLanguageArray must be used within ConfigProvider');
  }
  return context.timezone || '';
};

export const useIsMPLivliFamily = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('Is Livli Family must be used within ConfigProvider');
  }
  return context.config?.NEXT_PUBLIC_IS_MP_LIVLI_FAMILY || false;
};
